/**
 * Global Honor Hall season-end warnings (1h / 30m / 15m).
 * In-game only. Fetches status once (and on focus/dev), then uses timers —
 * no continuous polling for a once-a-month event.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import { useSeasonUiEnabled } from './hooks/useSeasonUiEnabled';
import { requestPendingSeasonEndCheck } from './utils/requestPendingSeasonEndCheck';
import { nextUpcomingThreshold } from './utils/seasonEndWarningSchedule';
import './SeasonEndDialog.css';

const WARNING_DURATION_MS = 12000;
/** Browsers clamp setTimeout; chunk long waits and re-arm from a fresh status fetch. */
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;

function warnedStorageKey(absoluteSeasonIndex, tier) {
  return `honorHallSeasonWarn:${absoluteSeasonIndex}:${tier}`;
}

/**
 * @param {{ isLoggedIn: boolean }} props
 */
export function SeasonEndWarnings({ isLoggedIn }) {
  const { t } = useTranslation();
  const enabled = useSeasonUiEnabled(!!isLoggedIn);
  const [message, setMessage] = useState(null);
  const dismissTimerRef = useRef(null);
  const armTimerRef = useRef(null);
  const seasonEndTimerRef = useRef(null);
  const endedCheckDispatchedRef = useRef(false);
  const fetchStatusRef = useRef(null);
  /** After a dev time-jump, do not flash the current tier on focus/status refresh. */
  const armTimersOnlyRef = useRef(false);

  const showWarning = useCallback((tier, absoluteSeasonIndex, { force = false } = {}) => {
    if (!tier || absoluteSeasonIndex == null) {
      throw new Error('SeasonEndWarnings.showWarning: tier and absoluteSeasonIndex are required');
    }
    const key = warnedStorageKey(absoluteSeasonIndex, tier);
    if (!force && sessionStorage.getItem(key) === '1') {
      return false;
    }
    sessionStorage.setItem(key, '1');
    setMessage(t(`seasonEnd.warning.${tier}`));
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setMessage(null), WARNING_DURATION_MS);
    return true;
  }, [t]);

  const clearArmTimer = useCallback(() => {
    if (armTimerRef.current) {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
  }, []);

  const clearSeasonEndTimer = useCallback(() => {
    if (seasonEndTimerRef.current) {
      clearTimeout(seasonEndTimerRef.current);
      seasonEndTimerRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearArmTimer();
    clearSeasonEndTimer();
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, [clearArmTimer, clearSeasonEndTimer]);

  const requestEndedCheck = useCallback(() => {
    if (endedCheckDispatchedRef.current) {
      return;
    }
    endedCheckDispatchedRef.current = true;
    requestPendingSeasonEndCheck();
  }, []);

  const scheduleIn = useCallback((delayMs, onFire) => {
    const clamped = Math.max(0, Math.min(delayMs, MAX_TIMER_MS));
    return setTimeout(onFire, clamped);
  }, []);

  const scheduleUpcomingWarning = useCallback((secondsUntilEnd, absoluteSeasonIndex) => {
    clearArmTimer();
    if (absoluteSeasonIndex == null || secondsUntilEnd == null) {
      return;
    }
    const upcoming = nextUpcomingThreshold(secondsUntilEnd);
    if (!upcoming) {
      return;
    }
    const delayMs = (secondsUntilEnd - upcoming.atSeconds) * 1000;
    if (delayMs > MAX_TIMER_MS) {
      armTimerRef.current = scheduleIn(MAX_TIMER_MS, () => {
        armTimerRef.current = null;
        if (typeof fetchStatusRef.current === 'function') {
          fetchStatusRef.current();
        }
      });
      return;
    }
    armTimerRef.current = scheduleIn(delayMs, () => {
      armTimerRef.current = null;
      showWarning(upcoming.tier, absoluteSeasonIndex);
      scheduleUpcomingWarning(upcoming.atSeconds, absoluteSeasonIndex);
    });
  }, [clearArmTimer, scheduleIn, showWarning]);

  const scheduleSeasonEndCheck = useCallback((secondsUntilEnd) => {
    clearSeasonEndTimer();
    if (secondsUntilEnd == null) {
      return;
    }
    if (secondsUntilEnd <= 0) {
      requestEndedCheck();
      return;
    }
    const delayMs = secondsUntilEnd * 1000;
    if (delayMs > MAX_TIMER_MS) {
      seasonEndTimerRef.current = scheduleIn(MAX_TIMER_MS, () => {
        seasonEndTimerRef.current = null;
        if (typeof fetchStatusRef.current === 'function') {
          fetchStatusRef.current();
        }
      });
      return;
    }
    seasonEndTimerRef.current = scheduleIn(delayMs, () => {
      seasonEndTimerRef.current = null;
      requestEndedCheck();
    });
  }, [clearSeasonEndTimer, scheduleIn, requestEndedCheck]);

  const applyStatus = useCallback((data, { force = false, armTimersOnly = false } = {}) => {
    if (!data || data.active === false) {
      clearArmTimer();
      clearSeasonEndTimer();
      return;
    }
    if (armTimersOnly) {
      armTimersOnlyRef.current = true;
    }
    const suppressCurrentTier = armTimersOnly || armTimersOnlyRef.current;
    // Login / focus (normal): show the current tier immediately.
    // Dev time-jumps arm timers only so we wait for the next threshold (live path).
    if (!suppressCurrentTier && data.warningTier && data.absoluteSeasonIndex != null) {
      showWarning(data.warningTier, data.absoluteSeasonIndex, { force });
    }
    if (data.secondsUntilEnd != null && data.absoluteSeasonIndex != null) {
      scheduleUpcomingWarning(data.secondsUntilEnd, data.absoluteSeasonIndex);
    }
    if (data.secondsUntilEnd != null) {
      if (data.secondsUntilEnd > 0) {
        endedCheckDispatchedRef.current = false;
      }
      scheduleSeasonEndCheck(data.secondsUntilEnd);
    }
  }, [
    showWarning,
    scheduleUpcomingWarning,
    scheduleSeasonEndCheck,
    clearArmTimer,
    clearSeasonEndTimer,
  ]);

  useEffect(() => {
    if (!enabled) {
      setMessage(null);
      armTimersOnlyRef.current = false;
      clearAllTimers();
      return undefined;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      try {
        const res = await fetch(`${API_URL}/honor-hall/season/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) {
            console.error('SeasonEndWarnings: status HTTP', res.status);
          }
          return;
        }
        if (cancelled) return;
        const data = await res.json();
        applyStatus(data, { armTimersOnly: armTimersOnlyRef.current });
      } catch (err) {
        if (!cancelled) {
          console.error('SeasonEndWarnings: status fetch failed', err);
        }
      }
    };

    fetchStatusRef.current = fetchStatus;

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      fetchStatus();
    };

    const devHandler = (event) => {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('honorHallSeasonWarn:')) {
          sessionStorage.removeItem(key);
        }
      });
      endedCheckDispatchedRef.current = false;
      setMessage(null);
      if (event?.detail?.armTimersOnly === true) {
        armTimersOnlyRef.current = true;
      } else {
        armTimersOnlyRef.current = false;
      }
      if (event?.detail?.secondsUntilEnd != null || event?.detail?.warningTier) {
        applyStatus(event.detail, {
          force: true,
          armTimersOnly: event.detail.armTimersOnly === true,
        });
        return;
      }
      fetchStatus();
    };

    fetchStatus();
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('coda-dev-season-changed', devHandler);
    return () => {
      cancelled = true;
      fetchStatusRef.current = null;
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('coda-dev-season-changed', devHandler);
      clearAllTimers();
    };
  }, [enabled, applyStatus, clearAllTimers]);

  if (!enabled || !message || typeof document === 'undefined') return null;

  return createPortal(
    <div className="honor-hall-season-global-warning" role="status">
      {message}
    </div>,
    document.body
  );
}

export default SeasonEndWarnings;
